import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link as MuiLink,
} from '@mui/material';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { SignupWizardProvider, useSignupWizard } from '../contexts/SignupWizardContext';
import { useAuth } from '../hooks/useAuth';
import {
  SignupStep1Contact,
  SignupStep2VerifyEmail,
  SignupStep3Plan,
  SignupStep4AccountDetails,
  SignupStep4Payment,
  SignupStep5Password,
  SignupStep6MFA,
} from './wizard/signup';
import { en } from '../translations/en';

import { verifyPayment } from '../utils/stripeService';
import { logger } from '../utils/logger';

interface SignupWizardContentProps {
  initialTier?: 'viewer' | 'creator' | 'commercial' | 'delegate' | null;
}

const SignupWizardContent: React.FC<SignupWizardContentProps> = ({ initialTier }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const {
    state,
    setStep,
    setSelectedTier,
    isPaidTier,
    setPaymentComplete,
    setSignupToken,
    setContactInfo,
    setError,
    setLoading,
    abandonAndReset,
    resetWizard,
    isResumingSession,
  } = useSignupWizard();

  const { loginWithTokens } = useAuth();

  // State for Start Over confirmation dialog
  const [showStartOverDialog, setShowStartOverDialog] = useState(false);

  // Ref to track intentional navigation (e.g., to Stripe payment)
  // This prevents the "Leave site?" warning when redirecting to Stripe
  const isIntentionalNavigation = useRef(false);

  // Set initial tier from navigation state
  useEffect(() => {
    if (initialTier) {
      setSelectedTier(initialTier);
    }
  }, [initialTier, setSelectedTier]);

  // Handle ?fresh=true query param - allows direct links to start fresh
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const freshStart = urlParams.get('fresh');

    if (freshStart === 'true') {
      // Abandon any existing session and start fresh
      abandonAndReset();
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [abandonAndReset]);

  // Start Over handlers
  const handleStartOverClick = () => {
    setShowStartOverDialog(true);
  };

  const handleStartOverCancel = () => {
    setShowStartOverDialog(false);
  };

  const handleStartOverConfirm = async () => {
    setShowStartOverDialog(false);
    await abandonAndReset();
  };

  // Determine if user has unsaved progress
  const hasUnsavedProgress = Boolean(
    state.signupToken || state.email || state.emailVerified || state.paymentComplete
  );

  // Warn user about leaving with unsaved progress
  // Skip warning for intentional navigation (e.g., Stripe payment redirect)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Allow intentional navigation without warning (e.g., Stripe redirect)
      if (isIntentionalNavigation.current) {
        return;
      }
      if (hasUnsavedProgress) {
        e.preventDefault();
        // Modern browsers ignore custom messages but require returnValue to be set
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedProgress]);

  // Define steps based on tier
  const getSteps = () => {
    if (state.selectedTier === 'delegate') {
      // Delegates skip plan selection entirely — don't show it in stepper
      return ['Contact Info', 'Verify Email', 'Account Details', 'Create Password', 'Setup MFA'];
    }

    const baseSteps = [
      'Contact Info',
      'Verify Email',
      'Select Plan',
    ];

    if (isPaidTier) {
      return [...baseSteps, 'ALI ID Prefix', 'Payment', 'Create Password', 'Setup MFA'];
    }

    return [...baseSteps, 'Create Password', 'Setup MFA'];
  };

  const steps = getSteps();

  // Map wizard step (1-7) to stepper step (0-based)
  const getStepperStep = () => {
    if (state.selectedTier === 'delegate') {
      // Delegate stepper: Contact Info(0), Verify Email(1), Account Details(2), Create Password(3), Setup MFA(4)
      // Internal steps: 1->0, 2->1, 3->1 (auto-skips), 4->2, 6->3, 7->4
      if (state.step <= 2) return state.step - 1;
      if (state.step === 3) return 1; // auto-skipping plan, still on verify visually
      if (state.step === 4) return 2;
      if (state.step === 6) return 3;
      if (state.step === 7) return 4;
      return state.step - 1;
    }
    if (!isPaidTier) {
      // For viewer: 1->0, 2->1, 3->2, 6->3, 7->4 (skip 4, 5)
      if (state.step <= 3) return state.step - 1;
      if (state.step === 6) return 3;
      if (state.step === 7) return 4;
      return 3;
    }
    // For paid: 1->0, 2->1, 3->2, 4->3, 5->4, 6->5, 7->6
    return state.step - 1;
  };

  const handleNext = async () => {
    const currentStep = state.step;

    // Call the step's submit handler
    const submitHandlers: Record<number, () => Promise<void>> = {
      1: (window as unknown as { __signupStep1Submit?: () => Promise<void> }).__signupStep1Submit,
      2: (window as unknown as { __signupStep2Submit?: () => Promise<void> }).__signupStep2Submit,
      3: (window as unknown as { __signupStep3Submit?: () => Promise<void> }).__signupStep3Submit,
      4: (window as unknown as { __signupStep4Submit?: () => Promise<void> }).__signupStep4Submit,
      6: (window as unknown as { __signupStep5Submit?: () => Promise<void> }).__signupStep5Submit,
    };

    const handler = submitHandlers[currentStep];
    if (handler) {
      await handler();
      // The step component will call onNext which sets the next step
    }
  };

  const handleBack = () => {
    const currentStep = state.step;

    // Determine previous step based on current step and tier
    if (currentStep === 1) {
      navigate('/auth');
      return;
    }

    let prevStep = currentStep - 1;

    // Delegate: skip payment going back (step 6 → step 4)
    if (state.selectedTier === 'delegate' && currentStep === 6) {
      prevStep = 4; // Back to Account Details
    }
    // Skip payment and account details steps for viewer
    else if (!isPaidTier && state.selectedTier !== 'delegate' && currentStep === 6) {
      prevStep = 3; // Go back to plan selection
    }

    setStep(prevStep as 1 | 2 | 3 | 4 | 5 | 6 | 7);
  };

  const handleStepNext = () => {
    const currentStep = state.step;
    let nextStep = currentStep + 1;

    // Delegate: skip plan selection (step 3 → step 4 account details)
    if (state.selectedTier === 'delegate' && currentStep === 3) {
      nextStep = 4;
    }
    // Delegate: skip payment (step 4 → step 6 password)
    else if (state.selectedTier === 'delegate' && currentStep === 4) {
      nextStep = 6;
    }
    // Viewer: skip account details + payment steps
    else if (!isPaidTier && state.selectedTier !== 'delegate' && currentStep === 3) {
      nextStep = 6; // Skip to password
    }

    if (nextStep <= 7) {
      setStep(nextStep as 1 | 2 | 3 | 4 | 5 | 6 | 7);
    }
  };

  const handleMfaComplete = async (authTokens?: { idToken: string; accessToken: string; refreshToken: string }) => {
    if (authTokens) {
      try {
        await loginWithTokens(authTokens);
        // Auto-login succeeded - clear wizard state then go directly to dashboard
        resetWizard();
        navigate('/', { replace: true });
        return;
      } catch (error) {
        logger.error('Auto-login after MFA setup failed', error);
        // Fall through to manual sign-in flow
      }
    }

    // No tokens or auto-login failed - clear wizard state then redirect to success page
    // Clearing here prevents the completed token from persisting in localStorage and
    // allowing a subsequent "Start over" to delete the already-live Cognito account (GL#47).
    resetWizard();
    navigate('/signup-success', {
      state: { tier: state.selectedTier, email: state.email, mfaMethod: state.mfaMethod },
    });
  };

  const handlePaymentRedirect = (stripeUrl: string) => {
    // Mark as intentional navigation to suppress "Leave site?" warning
    isIntentionalNavigation.current = true;
    // Store signup state in sessionStorage (fast path) AND ensure localStorage
    // context state is flushed (fallback for Safari which clears sessionStorage
    // on cross-domain navigation to Stripe checkout)
    sessionStorage.setItem('signupToken', state.signupToken || '');
    sessionStorage.setItem('signupEmail', state.email);
    sessionStorage.setItem('signupTier', state.selectedTier || '');
    window.location.href = stripeUrl;
  };

  // Handle return from payment (success or cancellation)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment_success');
    const sessionId = urlParams.get('session_id');
    const paymentCancelled = urlParams.get('payment_cancelled');

    // Handle cancelled payment
    if (paymentCancelled === 'true') {
      setError('Payment was cancelled. Please try again when ready.');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    // Handle successful payment
    if (paymentSuccess === 'true' && sessionId) {
      const verifyAndContinue = async () => {
        try {
          setLoading(true);
          setError(null);

          // Verify payment with the backend
          const result = await verifyPayment(sessionId);

          if (result.success) {
            // Restore state from sessionStorage, falling back to context state
            // (which was loaded from localStorage on mount).
            // Safari on iOS clears sessionStorage on cross-domain navigation
            // (e.g., Stripe checkout redirect), so the fallback is essential.
            const storedToken = sessionStorage.getItem('signupToken') || state.signupToken;
            const storedEmail = sessionStorage.getItem('signupEmail') || state.email;
            const storedTierRaw = sessionStorage.getItem('signupTier') || state.selectedTier;
            const storedTier = ['viewer', 'creator', 'commercial', 'delegate'].includes(storedTierRaw as string) ? (storedTierRaw as 'viewer' | 'creator' | 'commercial' | 'delegate') : state.selectedTier;

            if (storedToken) setSignupToken(storedToken);
            if (storedEmail) setContactInfo({ email: storedEmail });
            if (storedTier) setSelectedTier(storedTier);

            setPaymentComplete(true);
            setStep(6); // Go to password step

            // Clean up sessionStorage
            sessionStorage.removeItem('signupToken');
            sessionStorage.removeItem('signupEmail');
            sessionStorage.removeItem('signupTier');
            // Clean up URL
            window.history.replaceState({}, '', window.location.pathname);
          } else {
            setError('Payment verification failed. Please contact support.');
          }
        } catch (err) {
          setError('Failed to verify payment. Please try again.');
          logger.error('Payment verification error', err);
        } finally {
          setLoading(false);
        }
      };

      verifyAndContinue();
    }
  }, [setPaymentComplete, setSelectedTier, setStep, setSignupToken, setContactInfo, setError, setLoading]);

  const renderStepContent = () => {
    switch (state.step) {
      case 1:
        return (
          <SignupStep1Contact
            onNext={handleStepNext}
            selectedTier={state.selectedTier}
          />
        );
      case 2:
        return (
          <SignupStep2VerifyEmail
            onNext={handleStepNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <SignupStep3Plan
            onNext={handleStepNext}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <SignupStep4AccountDetails
            onNext={handleStepNext}
            onBack={handleBack}
            onPaymentRedirect={handlePaymentRedirect}
          />
        );
      case 5:
        return (
          <SignupStep4Payment
            onPaymentComplete={handleStepNext}
          />
        );
      case 6:
        return (
          <SignupStep5Password
            onNext={handleStepNext}
            onBack={handleBack}
          />
        );
      case 7:
        return (
          <SignupStep6MFA
            onComplete={handleMfaComplete}
            onBack={handleBack}
          />
        );
      default:
        return null;
    }
  };

  const getNextButtonLabel = () => {
    if (state.isLoading) return en.signup.actions.processing;

    // For MFA step (all tiers)
    if (state.step === 7) {
      return null; // MFA step handles its own buttons
    }

    // Plan selection - show appropriate label
    if (state.step === 3) {
      if (!state.selectedTier) return 'Select a Plan';
      return isPaidTier ? 'Continue' : 'Continue';
    }

    // Account details - delegate signups skip payment, paid tiers go to payment
    if (state.step === 4) {
      if (state.selectedTier === 'delegate') {
        return en.signup.actions.next;
      }
      return 'Continue to Payment';
    }

    return en.signup.actions.next;
  };

  const showNavigationButtons = state.step !== 5 && state.step !== 7;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: 4,
        px: 2,
      }}
    >
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        <Card sx={{ boxShadow: theme.shadows[4] }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography variant="h4" align="center" gutterBottom>
              {en.signup.title}
            </Typography>
            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
              {state.selectedTier
                ? `${state.selectedTier.charAt(0).toUpperCase() + state.selectedTier.slice(1)} ${en.signup.accountSetup}`
                : en.signup.accountSetup}
            </Typography>

            <Stepper
              activeStep={getStepperStep()}
              sx={{ mb: 4 }}
              orientation={isMobile ? 'vertical' : 'horizontal'}
              alternativeLabel={!isMobile}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {state.error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {state.error}
              </Alert>
            )}

            {renderStepContent()}

            {showNavigationButtons && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button onClick={handleBack} disabled={state.isLoading}>
                  {state.step === 1 ? en.signup.actions.cancel : en.signup.actions.back}
                </Button>

                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={state.isLoading || (state.step === 3 && !state.selectedTier)}
                  sx={{ py: 1.5 }}
                >
                  {getNextButtonLabel()}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{' '}
            <Link to="/auth" style={{ textDecoration: 'none', color: theme.palette.primary.main }}>
              Sign in
            </Link>
          </Typography>
        </Box>

        {/* Start Over link - only shown when resuming a session */}
        {isResumingSession && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <MuiLink
              component="button"
              variant="body2"
              onClick={handleStartOverClick}
              sx={{
                color: 'text.secondary',
                textDecoration: 'underline',
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              {en.signup.startOver.link}
            </MuiLink>
          </Box>
        )}
      </Box>

      {/* Start Over Confirmation Dialog */}
      <Dialog
        open={showStartOverDialog}
        onClose={handleStartOverCancel}
        aria-labelledby="start-over-dialog-title"
        aria-describedby="start-over-dialog-description"
      >
        <DialogTitle id="start-over-dialog-title">
          {en.signup.startOver.dialogTitle}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="start-over-dialog-description">
            {en.signup.startOver.dialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStartOverCancel} color="primary">
            {en.signup.startOver.cancel}
          </Button>
          <Button onClick={handleStartOverConfirm} color="error" variant="contained">
            {en.signup.startOver.confirm}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const Signup: React.FC = () => {
  const location = useLocation();
  const selectedTier = location.state?.selectedTier || null;
  const invitationContext = location.state?.invitationContext || null;

  return (
    <SignupWizardProvider>
      {invitationContext && (
        <Box sx={{ maxWidth: 900, mx: 'auto', mt: 2, px: 2 }}>
          <Alert severity="info">
            {en.invitation.signupBanner
              .replace('{collectionName}', invitationContext.collectionName)
              .replace('{delegatorName}', invitationContext.delegatorName)}
          </Alert>
        </Box>
      )}
      <SignupWizardContent initialTier={selectedTier} />
    </SignupWizardProvider>
  );
};

export default Signup;
